import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { useKundeList } from 'api/kunde';
import { SelectOption } from 'formComponents/Select/types';
import classnames from 'classnames';
import { viewStyles } from 'styles/viewStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { Select } from 'formComponents/Select/Select';
import { ValueType } from 'react-select';
import { Service, ServiceFilter, useServiceList } from 'api/service';
import { ServiceItem } from 'components/Service/ServiceItem';
import useDebouncedState from 'hooks/useDebouncedState';
import { ServicePosition, ServicePositionFilter, useServicePositionList } from 'api/servicePosition';
import { DebouncedSearchBox } from 'formComponents/DebouncedSearchBox';
import { ServiceEdit, ServiceEditData } from 'components/Service/ServiceEdit';
import { List } from '@fluentui/react/lib/List';
import { useHistory, useParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import moment from 'moment-timezone';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { RecordViewUrlParams } from 'layout/App/types';
import { createRecordUrl, decodeRecordNr } from 'utils/formatter';
import { ServicePositionenHeader } from 'components/Service/ServicePositionenHeader';
import { useTerminList } from 'api/termin';
import { ServiceStatusMulti } from 'formComponents/lookups/ServiceStatusMulti';
import { ServiceTypMulti } from '../../formComponents/lookups/ServiceTypMulti';

const defaultMaxRows = 250;

const styles = mergeStyleSets({
    searchBar1: {
        display: 'grid',
        gridTemplateColumns: '200px 150px 150px 180px 180px 1fr 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchBar2: {
        display: 'grid',
        gridTemplateColumns: '170px 170px 170px 170px',
        gridColumnGap: 10,
        marginTop: 10,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

interface FilterValues {
    maxRows: number;
    kNr?: number;
    nr?: string;
    auNr?: string;
    status: string;
    statusList: number[];
    typList: number[];
    betreuer: SelectOption;
    datumVon?: string;
    datumBis?: string;
    datBisVon?: string;
    datBisBis?: string;
    searchText: string;
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    kNr: undefined,
    nr: undefined,
    auNr: '',
    status: '__all__',
    statusList: [],
    typList: [],
    betreuer: alleBetreuerOption,
    datumVon: undefined,
    datumBis: undefined,
    datBisVon: undefined,
    datBisBis: undefined,
    searchText: '',
};

export const ServiceView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [viewMode, setViewMode] = usePersistentState<'short' | 'long'>('long', 'ServiceView.viewMode');

    const { urlKundeNr, urlRecordNr } = useParams<RecordViewUrlParams>();
    const kNr = urlKundeNr ? parseInt(urlKundeNr, 10) : undefined;

    // const isSmallScreenEmbedded = useMediaQuery({ query: `(max-width: 1800px)` });
    // const isSmallScreenNotEmbedded = useMediaQuery({ query: `(max-width: 1600px)` });
    // const isSmallScreen = urlKundeNr ? isSmallScreenEmbedded : isSmallScreenNotEmbedded;

    const serviceEditData = React.useMemo<ServiceEditData | null>(() => {
        if (urlRecordNr) {
            return {
                sNr: decodeRecordNr(urlRecordNr),
                kNr,
            };
        }
        return null;
    }, [kNr, urlRecordNr]);

    const kundeList = useKundeList({ nr: kNr || -1 });

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        if (auth.rolle === 'AD') {
            return {
                ...defaultFilterValues,
                betreuer: {
                    value: auth.kz || '',
                    label: auth.name || '?',
                },
            };
        }
        return {
            ...defaultFilterValues,
            betreuer: alleBetreuerOption,
        };
    }, 1000);

    const filter = React.useMemo<ServiceFilter>(() => {
        return {
            kNr,
            betreuer:
                debouncedFilterValues.betreuer.value === '__all__'
                    ? undefined
                    : parseInt(debouncedFilterValues.betreuer.value, 10),
            // status: debouncedFilterValues.status === '__all__' ? undefined :
            // parseInt(debouncedFilterValues.status, 10),
            statusList: debouncedFilterValues.statusList,
            typList: debouncedFilterValues.typList,
            nrSearch: debouncedFilterValues.nr,
            auNrSearch: debouncedFilterValues.auNr,
            datumVon: debouncedFilterValues.datumVon,
            datumBis: debouncedFilterValues.datumBis,
            datBisVon: debouncedFilterValues.datBisVon,
            datBisBis: debouncedFilterValues.datBisBis,
            searchText: debouncedFilterValues.searchText,
            withKunde: true,
            maxRows: defaultMaxRows,
        };
    }, [kNr, debouncedFilterValues]);

    const serviceList = useServiceList(filter);

    const positionenFilter = React.useMemo<ServicePositionFilter>(() => {
        if (serviceList.isLoading || serviceList.isLoading || serviceList.isError) {
            return {};
        }

        return {
            sNrList: serviceList.data?.map((service) => service.nr) || [],
        };
    }, [serviceList]);

    const servicePositionList = useServicePositionList(positionenFilter, { enabled: false });

    React.useEffect(() => {
        if (positionenFilter.sNrList) {
            servicePositionList.refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionenFilter]);

    const servicePositionenByANr = React.useMemo<{ [key: string]: ServicePosition[] }>(() => {
        if (
            servicePositionList.isLoading ||
            servicePositionList.isError ||
            (servicePositionList.data?.length || 0) === 0
        ) {
            return {};
        }

        const newServicePositionenByANr: { [key: string]: ServicePosition[] } = {};

        servicePositionList.data?.forEach((servicePosition) => {
            if (!newServicePositionenByANr[servicePosition.sNr]) {
                newServicePositionenByANr[servicePosition.sNr] = [];
            }

            newServicePositionenByANr[servicePosition.sNr].push(servicePosition);
        });

        return newServicePositionenByANr;
    }, [servicePositionList]);

    const kNrArtikelGruppeNrAndArtikelNrList = React.useMemo(() => {
        const list: string[] = [];

        (serviceList.data || []).forEach((service) => {
            const positionen = servicePositionenByANr[service.nr];
            if (positionen) {
                positionen.forEach((p) => {
                    if (p.artikelGruppeNr && p.artikelNr) {
                        list.push(service.kNr + '|' + p.artikelGruppeNr + '|' + p.artikelNr);
                    }
                });
            }
        });

        return list;
    }, [serviceList.data, servicePositionenByANr]);

    const wiedervorlagen = useTerminList({
        terminStatus: 0,
        terminArt: 'W',
        kNrArtikelGruppeNrAndArtikelNrList,
    });

    const onRenderCell = React.useCallback(
        (service: Service | undefined): JSX.Element => {
            if (!service) {
                return <div />;
            }

            return (
                <ServiceItem
                    key={service.nr}
                    service={service}
                    withKunde={!urlKundeNr}
                    viewMode={viewMode}
                    withHeader={false}
                    servicePositionen={servicePositionenByANr[service.nr] || []}
                    wiedervorlagen={wiedervorlagen.data || []}
                    editService={() => history.push(createRecordUrl('services', urlKundeNr, service.nr))}
                    refetch={() => {
                        wiedervorlagen.refetch().then();
                        servicePositionList.refetch().then(() => {
                            serviceList.refetch().then();
                        });
                    }}
                />
            );
        },
        [urlKundeNr, viewMode, servicePositionenByANr, wiedervorlagen, history, servicePositionList, serviceList],
    );

    const renderDatumVon = () => (
        <FormDatePicker
            placeholder="Datum (von)"
            value={filterValues.datumVon}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    const renderDatumBis = () => (
        <FormDatePicker
            placeholder="Datum (bis)"
            value={filterValues.datumBis}
            onChange={(newValue) => {
                setFilterValues({
                    ...filterValues,
                    datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                });
            }}
        />
    );

    if (auth.rolle === 'AD' && kundeList.data?.length === 1) {
        if (kundeList.data[0].betreuer && kundeList.data[0].betreuer !== auth.nr) {
            return (
                <Fabric className={viewStyles.container}>
                    <div className={viewStyles.listContainer}>
                        <div className={viewStyles.noData}>Keine Daten.</div>
                    </div>
                </Fabric>
            );
        }
    }

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric
            className={classnames(viewStyles.container, viewStyles.twoSearchBarLines, {
                [viewStyles.embedded]: !!urlKundeNr,
            })}
        >
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    {serviceEditData?.sNr ? 'Service ' + serviceEditData?.sNr : 'Services'}
                </title>
            </Helmet>
            <div className={styles.searchBar1}>
                <DebouncedSearchBox
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Service-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <DebouncedSearchBox
                    placeholder="Auftrag-Nr."
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            auNr: newValue === undefined ? '' : newValue,
                        });
                    }}
                />
                <Select
                    name="betreuer"
                    value={filterValues.betreuer}
                    options={betreuerFilterOptions}
                    onChange={(newValue: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuer: newValue || alleBetreuerOption,
                        });
                    }}
                />
                <ServiceTypMulti
                    value={filterValues.typList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            typList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <ServiceStatusMulti
                    value={filterValues.statusList.map((v) => v.toString())}
                    renderLabel="placeholder"
                    onChange={(newValues) => {
                        setFilterValues({
                            ...filterValues,
                            statusList: newValues.map((v) => parseInt(v, 10)),
                        });
                    }}
                />

                <div />

                <div>
                    <Checkbox
                        className={formStyles.checkboxInsideForm}
                        label="Kurzansicht"
                        checked={viewMode === 'short'}
                        onChange={(event, checked) => {
                            setViewMode(checked ? 'short' : 'long');
                        }}
                    />
                </div>

                {auth.rolle !== 'AD' && (
                    <PrimaryButton
                        onClick={() => history.push(createRecordUrl('services', urlKundeNr, 'neu'))}
                        text="Neues Service"
                    />
                )}
            </div>

            <div className={styles.searchBar2}>
                {renderDatumVon()}
                {renderDatumBis()}
            </div>

            {serviceList.isLoading || serviceList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <div className={viewStyles.listContainer}>
                    {serviceList?.data && serviceList?.data.length === 0 ? (
                        <div className={viewStyles.noData}>Keine Services.</div>
                    ) : (
                        <>
                            {viewMode === 'short' && (
                                <div className={viewStyles.listContainer}>
                                    <table className={formStyles.stickyPositionenHeaderTable}>
                                        <ServicePositionenHeader />
                                    </table>
                                </div>
                            )}
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div
                                        style={{ width, height: height - 25, overflowX: 'hidden', overflowY: 'scroll' }}
                                    >
                                        <List
                                            key={
                                                viewMode +
                                                (servicePositionList.isSuccess ? 1 : 0) +
                                                (wiedervorlagen.isSuccess ? 1 : 0)
                                            }
                                            items={serviceList.data || []}
                                            onRenderCell={onRenderCell}
                                        />
                                    </div>
                                )}
                            </AutoSizer>
                        </>
                    )}
                </div>
            )}
            {serviceEditData !== null && (
                <ServiceEdit
                    defaultKNr={serviceEditData.kNr}
                    sNr={serviceEditData.sNr}
                    onClose={(refetch) => {
                        history.push(createRecordUrl('services', urlKundeNr));
                        if (refetch) {
                            wiedervorlagen.refetch().then();
                            servicePositionList.refetch().then(() => {
                                serviceList.refetch().then();
                            });
                        }
                    }}
                    onReopen={(newANr: string) => {
                        history.push(createRecordUrl('services', urlKundeNr));
                        servicePositionList.refetch().then(() => {
                            serviceList.refetch().then(() => {
                                history.push(createRecordUrl('services', serviceEditData.kNr?.toString(), newANr));
                            });
                        });
                    }}
                />
            )}
        </Fabric>
    );
};
